/**
 * Inline-markdown → React renderer for public-API notification bodies.
 *
 * Supports inline constructs ONLY — bold, italic, inline code, and links —
 * matching the public-API contract, the client-core `stripInlineMarkdown`
 * (toast path), and Android's PublicApiRenderer. No block elements
 * (headings, lists, blockquotes, images): a notification body is a single
 * line of prose.
 *
 * SECURITY: output is React nodes, never an HTML string, so React escapes
 * all text content — a body like `<script>` renders literally, not as
 * markup. There is no `dangerouslySetInnerHTML` anywhere. The only
 * injection vector is a link `href`, which is restricted to http/https;
 * any other scheme (`javascript:`, `data:`, …) falls back to rendering the
 * raw `[text](url)` token as plain text.
 */

import type { ReactNode } from "react";

// One alternation per supported token, tried left-to-right. `[^*\n]` /
// `[^_\n]` etc. keep each match on one line and non-greedy-ish so adjacent
// tokens don't merge. Links first so a `[a](b)` isn't half-eaten by an
// emphasis rule.
// The url portion uses a balanced-paren scanner — base chars `[^()]+` plus
// zero-or-more `(…)` groups — so a url with one level of parens (e.g. a
// Wikipedia `..._(language)` link) is captured in full instead of truncating
// at the first `)`. Mirrors Android's PublicApiRenderer link regex.
const TOKEN =
  /(\[[^\]]+\]\([^()]+(?:\([^()]*\)[^()]*)*\))|(\*\*[^*\n]+\*\*)|(__[^_\n]+__)|(\*[^*\n]+\*)|(_[^_\n]+_)|(`[^`\n]+`)/g;

const LINK = /^\[([^\]]+)\]\(([^()]+(?:\([^()]*\)[^()]*)*)\)$/;

/** http(s)-only — blocks javascript:/data:/file: hrefs. */
function isSafeHref(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Parse inline markdown into an array of React nodes. Plain text passes
 * through unchanged (returned as a single-string array). Safe to drop
 * straight into JSX: `<p>{renderInlineMarkdown(body)}</p>`.
 */
export function renderInlineMarkdown(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of text.matchAll(TOKEN)) {
    const start = match.index ?? 0;
    if (start > lastIndex) nodes.push(text.slice(lastIndex, start));
    const token = match[0];

    if (token.startsWith("[")) {
      const linkMatch = LINK.exec(token);
      if (linkMatch && isSafeHref(linkMatch[2])) {
        nodes.push(
          <a key={key++} href={linkMatch[2]} target="_blank" rel="noopener noreferrer">
            {linkMatch[1]}
          </a>
        );
      } else {
        // Unsafe or malformed link → show the literal token, no anchor.
        nodes.push(token);
      }
    } else if (token.startsWith("**") || token.startsWith("__")) {
      nodes.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("`")) {
      nodes.push(<code key={key++}>{token.slice(1, -1)}</code>);
    } else {
      // *italic* or _italic_
      nodes.push(<em key={key++}>{token.slice(1, -1)}</em>);
    }

    lastIndex = start + token.length;
  }

  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}
